import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../../state'
import * as modals from '../../modals'

/**
 * Component for displaying the organizations page, which shows a table of
 * organization entities (e.g. divisions, departments) based on the organization
 * dimension in the schema. The user can filter the table by selecting an
 * organization entity from a dropdown, which updates the collection's filters
 * accordingly.
 */
@ngCore.Component({
  imports: [
    ds.layout.page.Component,
    ds.layout.page.child.header.Component,
    ds.table.Component,
    ds.input.select.single.Component,
    modals.jointUse.Component,
  ],
  selector: 'atn-space-organizations',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.space.organizations.Feature} */
  private readonly _organizationsFeature = ngCore.inject(
    state.space.organizations.Feature,
  )

  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)

  /**
   * The organization dimension from the schema, used for deriving the page name
   * and filter items. This is optional because the schema might not have the
   * expected dimension, in which case the page will display fallback values.
   */
  private readonly _orgDimensionSg = ngCore.computed(
    (schema = this._schemaFeature.selectors.schema.asSignal()) =>
      schema.tryGetDimension(state.space.organizations.constants.DIMENSION_ID),
  )

  /**
   * The organization entities from the organization dimension, used for
   * deriving the filter items and selected entity. This is optional because the
   * dimension might not have any entities, in which case the filter will be
   * empty and the page will display fallback values.
   */
  private readonly _orgEntitiesSg = ngCore.computed(
    (orgDimension = this._orgDimensionSg()) => orgDimension?.entities ?? [],
  )

  /**
   * The items to display in the organization entity filter dropdown, derived
   * from the organization entities. Each item has an `id` and `name` property,
   * where `id` is the entity ID and `name` is the entity name.
   */
  public readonly orgEntitySelectItems = ngCore.computed<
    readonly ds.input.select.models.Item<ents.core.dms.models.Entity.Id>[]
  >((orgEntities = this._orgEntitiesSg()) =>
    orgEntities.map(({ id: value, name: label }) => ({
      label,
      value,
    })),
  )

  /**
   * The currently selected organization entity ID for filtering.
   *
   * NOTE: Defaults to the root organization (entities should be sorted with
   * root first) @see {@link _orgEntitiesSg}.
   */
  public readonly selectedOrgEntityIdSg = ngCore.linkedSignal<
    readonly ents.core.dms.models.Entity[],
    ents.core.dms.models.Entity.Id | undefined
  >({
    computation: (orgEntities, prev) =>
      (prev?.value && orgEntities.find(({ id }) => id === prev.value)?.id) ??
      orgEntities[0]?.id,
    source: this._orgEntitiesSg,
  })

  /**
   * The currently selected organization entity based on the selected ID, used
   * for displaying the active filter and deriving the table name.
   */
  private readonly _selectedEntitySg = ngCore.computed(
    (
      orgEntities = this._orgEntitiesSg(),
      selectedOrgEntityId = this.selectedOrgEntityIdSg(),
    ) => orgEntities.find(({ id }) => id === selectedOrgEntityId),
  )

  /**
   * Current entity-scoped Organizations collection
   */
  public readonly organizationsEcSg = ngCore.computed(
    (entityId = this.selectedOrgEntityIdSg()) =>
      entityId
        ? this._organizationsFeature.selectors
            .getOrganizationEc(entityId)
            .asSignal()
        : undefined,
  )

  /** Canonical table input for the selected organization entity. */
  public readonly tableConfigSg = ngCore.computed(
    (collection = this.organizationsEcSg(), title = this.tableNameSg()) =>
      collection
        ? ({
            collection,
            view: { showColumnSettings: false, title },
          } satisfies ds.table.ec.Config.Input<state.space.organizations.TypeParams>)
        : undefined,
  )

  /** Whether the joint-use modal is editing an organization */
  public readonly isUpdatingJointUseSg = ngCore.computed(
    (organizationsEc = this.organizationsEcSg()) =>
      organizationsEc?.plugins.currentEntityPlugin.feature.selectors
        .isUpdating({
          target:
            state.space.organizations.plugins.jointUse.CURRENT_ENTITY_TARGET,
        })
        .asSignal() ?? false,
  )

  /**
   *  The name of the page to display, derived from the organization dimension,
   *  pluralized.
   */
  public readonly pageNameSg = ngCore.computed(
    (orgDimension = this._orgDimensionSg()) =>
      // TODO: Handle pluralization properly
      (orgDimension && `${orgDimension.name}s`) ?? 'Organizations',
  )

  /**
   * The name of the table to display, derived from the selected organization
   * entity. For example, if "Division" is selected, this would return
   * "Divisions".
   */
  public readonly tableNameSg = ngCore.computed(
    (selectedItem = this._selectedEntitySg()) =>
      // TODO: Handle pluralization properly
      (selectedItem && `${selectedItem.name}s`) ?? 'Organization Entities',
  )
}
